import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useT } from '../../i18n/useT';
import {
  SocialLoginSuccess,
  submitAppleBirthday,
  submitGoogleBirthday,
} from '../../services/socialAuthApi';

export type SocialBirthdayContext = {
  provider: 'google' | 'apple';
  email?: string;
  appleId?: string;
};

type Props = {
  open: boolean;
  context: SocialBirthdayContext | null;
  onComplete: (result: SocialLoginSuccess) => void;
  onClose: () => void;
};

export default function SocialBirthdayDialog({ open, context, onComplete, onClose }: Props) {
  const t = useT();
  const [birthday, setBirthday] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (saving) return;
    setBirthday('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!context || !birthday) {
      setError(t('socialBirthdayRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result =
        context.provider === 'google'
          ? await submitGoogleBirthday(context.email || '', birthday)
          : await submitAppleBirthday(context.appleId || '', birthday, context.email);

      if (result.kind === 'error') {
        setError(result.message);
        return;
      }
      onComplete(result);
      setBirthday('');
    } catch {
      setError(t('socialBirthdayFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('socialBirthdayTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('socialBirthdaySubtitle')}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          type="date"
          label={t('socialBirthdayLabel')}
          value={birthday}
          onChange={(e) => setBirthday(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving || !birthday}>
          {saving ? <CircularProgress size={22} /> : t('socialBirthdayContinue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
